import { canTransitionInvoiceStatus } from "@common/helpers/invoice-status-machine";
import { InvoiceStatus } from "@common/types/invoice";
import { getInvoiceStatusLabel } from "./getInvoiceStatusLabel";

export enum QuickBooksSettlementExtent {
    NONE = "none",
    PARTIAL = "partial",
    FULL = "full",
}

interface GetInvoiceStatusTransitionDisabledReasonParams {
    from: InvoiceStatus;
    to: InvoiceStatus;
    hasMatches: boolean;
    hasCounterparty?: boolean;
    quickBooksSettlement?: QuickBooksSettlementExtent;
}

/**
 * Explains why a manual status transition isn't offered in the status select,
 * mirroring the authoritative backend rules in
 * `common/helpers/invoice-status-machine.ts` (see `canTransitionInvoiceStatus`).
 *
 * Returns `null` when the transition is allowed (option should be enabled).
 */
export function getInvoiceStatusTransitionDisabledReason({
    from,
    to,
    hasMatches,
    hasCounterparty = true,
    quickBooksSettlement = QuickBooksSettlementExtent.NONE,
}: GetInvoiceStatusTransitionDisabledReasonParams): string | null {
    if (canTransitionInvoiceStatus(from, to, { hasMatches, hasCounterparty })) {
        return null;
    }

    if (quickBooksSettlement !== QuickBooksSettlementExtent.NONE) {
        if (
            quickBooksSettlement === QuickBooksSettlementExtent.FULL &&
            from === InvoiceStatus.PAID &&
            to === InvoiceStatus.OPEN
        ) {
            return "This invoice was settled in QuickBooks before your bookkeeping start date, so it can't be reopened in Kick.";
        }
        if (to === InvoiceStatus.VOID) {
            return quickBooksSettlement === QuickBooksSettlementExtent.PARTIAL
                ? "Part of this invoice was settled in QuickBooks before your bookkeeping start date, so it can't be voided in Kick."
                : "This invoice was settled in QuickBooks before your bookkeeping start date, so it can't be voided in Kick.";
        }
    }

    if (from === InvoiceStatus.PAID && to === InvoiceStatus.OPEN) {
        return "This invoice was settled via matched transactions — unmatch them to reopen it instead.";
    }

    if (
        to === InvoiceStatus.VOID &&
        hasMatches &&
        (from === InvoiceStatus.OPEN || from === InvoiceStatus.UNCOLLECTABLE)
    ) {
        return "This invoice has matched transactions - unmatch them before voiding it.";
    }

    if (from === InvoiceStatus.VOID) {
        return "Voided invoices are final and can't change status.";
    }

    if (to === InvoiceStatus.DRAFT) {
        return "Finalized invoices can't be reverted to Draft - void and reissue instead.";
    }

    if (from === InvoiceStatus.DRAFT && !hasCounterparty) {
        return "A counterparty is required before this invoice can leave Draft status.";
    }

    if (from === InvoiceStatus.DRAFT) {
        return `Finalize the invoice (mark it Open) before it can be marked as ${getInvoiceStatusLabel(to)}.`;
    }

    if (from === InvoiceStatus.UNCOLLECTABLE && to === InvoiceStatus.OPEN) {
        return "Recovering an uncollectible invoice happens via Paid - mark it Paid first, then reopen.";
    }

    if (from === InvoiceStatus.PAID) {
        return `Reopen the invoice first, then mark it ${getInvoiceStatusLabel(to)}.`;
    }

    return "This status change isn't supported.";
}
